import { useCallback, useEffect, useMemo, useRef } from "react";
import { useRouter } from "next/router";
import { useSearchProvider } from "../use-search-provider";
import {
  useListingServerSnapshot,
  useListingState,
} from "../stores/use-listing-store";
import { listingStore, pagesUpTo } from "src/stores/listing/listing.store";
import {
  loadListingState,
  loadScrollPosition,
  saveListingState,
  saveScrollPosition,
} from "src/utils/listing-persistence";
import {
  applySelectedFacetsToFacets,
  loadSelectedFacetsFromUrl,
  loadSortFromUrl,
  selectedFacetsEqual,
} from "src/utils/search-provider/helpers";
import {
  buildCategoryTree,
  omitParentSlugs,
} from "src/utils/category-tree-helpers";
import { useHandleSearchParams } from "../use-handle-search-params";
import type { Gender, ProductListType } from "src/types/product-listing";
import { localeFix, ShopType } from "src/utils/genderLocale/genderLocale";
import { isListingSortKey } from "src/utils/type-guards";
import {
  indexToUrlPage,
  PAGE_QUERY_PARAM,
  toPublicAsPath,
  urlPageToIndex,
} from "src/utils/pagination/page-url";
import { isPaginatedPlpPath } from "src/utils/pagination/plp-paths";

function useProductList({
  productListType,
  facetValue,
  categoryId,
  collectionId,
  gender,
}: {
  productListType: ProductListType;
  facetValue: string;
  categoryId?: string;
  /** Dual-run fallback identifier — see ListingParams.collectionId. */
  collectionId?: string;
  gender?: Gender;
}) {
  const router = useRouter();
  const [searchProvider, isReady] = useSearchProvider();
  const { handleSearchParams } = useHandleSearchParams();
  const serverSnapshot = useListingServerSnapshot();

  const {
    facets,
    tree,
    appliedFacets,
    page,
    productsByPage,
    sort,
    totalPages,
    totalResults,
    resultId,
    loading,
    sortOption,
    sortOptions,
    hasFetched,
  } = useListingState();

  useEffect(() => {
    if (typeof window === "undefined" || !("scrollRestoration" in history)) {
      return;
    }

    const previous = history.scrollRestoration;
    history.scrollRestoration = "manual";

    return () => {
      history.scrollRestoration = previous;
    };
  }, []);

  const hydrateRef = useRef(false);
  const hydratedSlugRef = useRef<string | null>(null);
  const pendingScrollRef = useRef<number | null>(null);
  const fetchedKeyRef = useRef<string | null>(null);
  const appDrivenPageChangeRef = useRef(false);

  const hydrationKey = `${facetValue}|${gender ?? ""}`;

  useEffect(() => {
    const currentPage = urlPageToIndex(
      new URLSearchParams(location.search).get(PAGE_QUERY_PARAM)
    );

    if (hydratedSlugRef.current === hydrationKey) {
      const url = new URL(location.toString());
      const urlFacets = loadSelectedFacetsFromUrl(url.searchParams);
      const {
        appliedFacets: storeFacets,
        page: storePage,
        products: storeProducts,
      } = listingStore.getSnapshot();

      if (!selectedFacetsEqual(storeFacets, urlFacets)) {
        listingStore.setAppliedFacets(urlFacets);
      }

      if (currentPage !== storePage) {
        if (appDrivenPageChangeRef.current) {
          appDrivenPageChangeRef.current = false;
          listingStore.setPage(currentPage);
        } else {
          listingStore.setState({
            page: currentPage,
            products: pagesUpTo(storeProducts, currentPage),
          });
          fetchedKeyRef.current = null;
        }
      }

      return;
    }

    hydratedSlugRef.current = hydrationKey;

    const url = new URL(location.toString());
    const urlFacets = loadSelectedFacetsFromUrl(url.searchParams);
    const urlSort = loadSortFromUrl(url.searchParams);
    const persisted = loadListingState(router.asPath);

    if (persisted) {
      const reconciledFacets = persisted.facets
        ? applySelectedFacetsToFacets(persisted.facets, urlFacets)
        : undefined;
      const resolvedSort = urlSort || persisted.sort;
      const resolvedSortOption = isListingSortKey(resolvedSort)
        ? persisted.sortOptions[resolvedSort]
        : undefined;

      listingStore.hydrate({
        products: pagesUpTo(persisted.products, currentPage),
        page: currentPage,
        sort: resolvedSort,
        sortOption: resolvedSortOption,
        sortOptions: persisted.sortOptions,
        totalPages: persisted.totalPages,
        totalResults: persisted.totalResults,
        facets: reconciledFacets,
        tree: persisted.tree,
        appliedFacets: urlFacets,
      });

      pendingScrollRef.current = loadScrollPosition(router.asPath) || 0;
    } else {
      const seededSortOptions = listingStore.getSnapshot().sortOptions;
      const resolvedSortOption = urlSort
        ? seededSortOptions[urlSort]
        : undefined;

      listingStore.hydrate({
        products: {},
        page: currentPage,
        sort: urlSort || "",
        sortOption: resolvedSortOption,
        totalPages: 0,
        appliedFacets: urlFacets,
      });
    }

    hydrateRef.current = true;
  }, [hydrationKey, router.asPath]);

  useEffect(() => {
    if (tree || !isReady || !searchProvider?.categoryTree) {
      return;
    }

    let cancelled = false;

    void searchProvider
      .categoryTree({
        gender,
        isLoggedIn: false,
        productListType,
        slug: facetValue,
        query: facetValue,
      })
      .then((result) => {
        if (cancelled || listingStore.getSnapshot().tree) {
          return;
        }

        const filtered =
          gender && gender !== ShopType.All
            ? result.categories.filter((item) =>
                item.genders.includes(localeFix({ gender }))
              )
            : result.categories;

        listingStore.setTree(buildCategoryTree(filtered));
      })
      .catch((err) => {});

    return () => {
      cancelled = true;
    };
  }, [tree, gender, isReady, searchProvider, productListType, facetValue]);

  useEffect(() => {
    if (tree || searchProvider?.categoryTree) {
      return;
    }

    const sourceFacets = facets ?? serverSnapshot.facets;
    const categoryFacet = sourceFacets?.get("category");

    if (categoryFacet?.type !== "category") {
      return;
    }

    const filteredCategories =
      gender && gender !== ShopType.All
        ? categoryFacet.data.filter((item) =>
            item.genders.includes(localeFix({ gender }))
          )
        : categoryFacet.data;

    listingStore.setTree(buildCategoryTree(filteredCategories));
  }, [tree, facets, serverSnapshot, gender, searchProvider]);

  useEffect(() => {
    if (
      !hydrateRef.current ||
      loading ||
      !isReady ||
      !searchProvider ||
      (productListType === "SEARCH_PLP" && !facetValue)
    ) {
      return;
    }

    if (page !== listingStore.getSnapshot().page) {
      return;
    }

    if (page in productsByPage) {
      return;
    }

    const { revision } = listingStore.getSnapshot();
    const fetchKey = `${revision}|${page}`;

    if (fetchedKeyRef.current === fetchKey) {
      return;
    }

    fetchedKeyRef.current = fetchKey;
    listingStore.setLoading(true);

    const {
      tree: currentTree,
      facets: currentFacets,
      products: currentProducts,
      appliedFacets: currentAppliedFacets,
    } = listingStore.getSnapshot();
    const requestAppliedFacets =
      currentTree && currentAppliedFacets.category
        ? {
            ...currentAppliedFacets,
            category: omitParentSlugs(
              currentAppliedFacets.category,
              currentTree
            ),
          }
        : currentAppliedFacets;

    searchProvider
      .listing({
        gender,
        isLoggedIn: false,
        page,
        productListType,
        slug: facetValue,
        categoryId,
        collectionId,
        query: facetValue,
        appliedFacets: requestAppliedFacets,
        currentFacets,
        sort: sortOption?.value,
        sortDirection: sortOption?.direction,
      })
      .then(
        ({
          products,
          totalPages,
          facets,
          totalResults,
          sortOptions,
          resultId,
          request,
        }) => {
          listingStore.setState({
            page,
            products: { ...currentProducts, [page]: products },
            totalPages,
            totalResults,
            resultId,
            request,
            sortOptions,
            facets: applySelectedFacetsToFacets(facets, currentAppliedFacets),
            revision,
            loading: false,
            hasFetched: true,
          });
        }
      )
      .catch((error) => {
        console.error("[SearchProvider]: Error fetching listing.", error);
        listingStore.setState({ loading: false, hasFetched: true });
      });
  }, [
    searchProvider,
    productListType,
    appliedFacets,
    page,
    productsByPage,
    loading,
    isReady,
    sortOption,
    facetValue,
    gender,
  ]);

  useEffect(() => {
    if (!hydrateRef.current) {
      return;
    }

    saveListingState(router.asPath, {
      products: productsByPage,
      page,
      sort,
      sortOptions,
      totalPages,
      totalResults,
      facets,
      tree,
    });
  }, [
    router.asPath,
    productsByPage,
    page,
    sort,
    sortOptions,
    totalPages,
    totalResults,
    facets,
    tree,
  ]);

  useEffect(() => {
    if (
      pendingScrollRef.current === null ||
      !productsByPage[page]?.length ||
      loading
    ) {
      return;
    }

    const scrollY = pendingScrollRef.current;
    pendingScrollRef.current = null;

    window.scrollTo({ top: scrollY });
  }, [productsByPage, page, loading]);

  const routeChangeRef = useRef(false);

  useEffect(() => {
    const handleRouteChange = (url: string) => {
      const {
        products,
        page,
        sort,
        sortOptions,
        totalPages,
        totalResults,
        facets,
        tree,
      } = listingStore.getSnapshot();

      const [currentPath] = router.asPath.split("?") || [];
      const [nextPath] = url.split("?") || [];

      saveListingState(router.asPath, {
        products,
        page,
        sort,
        sortOptions,
        totalPages,
        totalResults,
        facets,
        tree,
      });

      if (currentPath !== nextPath) {
        saveScrollPosition(router.asPath, window.scrollY);
      }
    };

    router.events.on("routeChangeStart", handleRouteChange);
    routeChangeRef.current = true;
    return () => {
      router.events.off("routeChangeStart", handleRouteChange);
    };
  }, [router]);

  const products = useMemo(() => {
    return Object.keys(productsByPage)
      .map(Number)
      .sort((a, b) => a - b)
      .flatMap((page) => productsByPage[page]);
  }, [productsByPage]);

  const loadMore = useCallback(() => {
    const { page } = listingStore.getSnapshot();

    appDrivenPageChangeRef.current = true;
    handleSearchParams({
      facet: "page",
      value: String(indexToUrlPage(page + 1)),
      isSingle: true,
    });
  }, [handleSearchParams]);

  const loadPrevious = useCallback(() => {
    const { page } = listingStore.getSnapshot();
    const previousPage = page - 1;

    appDrivenPageChangeRef.current = true;

    if (previousPage <= 0) {
      handleSearchParams({
        facet: "page",
        value: String(indexToUrlPage(page)),
        shouldDelete: true,
      });

      return;
    }

    handleSearchParams({
      facet: "page",
      value: String(indexToUrlPage(previousPage)),
      isSingle: true,
    });
  }, [handleSearchParams]);

  const hasNextPage = useMemo(
    () =>
      totalPages > 0 &&
      page + 1 < totalPages &&
      !Object.keys(productsByPage).includes(String(page + 1)),
    [totalPages, page, productsByPage]
  );

  const nextPageHref = useMemo(() => {
    if (!hasNextPage) {
      return null;
    }

    const [pathname] = toPublicAsPath(router.asPath).split("?");

    return isPaginatedPlpPath(pathname)
      ? `?${PAGE_QUERY_PARAM}=${indexToUrlPage(page) + 1}`
      : null;
  }, [hasNextPage, router.asPath, page]);

  const hasPreviousPage = useMemo(
    () =>
      totalPages > 0 && page > 0 && !Object.keys(productsByPage).includes("0"),
    [page, totalPages, productsByPage]
  );

  return {
    products,
    facets,
    page,
    nextPageHref,
    hasNextPage,
    hasPreviousPage,
    loading,
    totalPages,
    totalResults,
    resultId,
    loadMore,
    loadPrevious,
    hasFetched,
  };
}

export { useProductList };
